import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css'
import RangeSlider from 'react-bootstrap-range-slider'
import 'react-phone-number-input/style.css'
import { Autocomplete, Box, TextField } from '@mui/material'
import PhoneInput from 'react-phone-number-input'
import NumberFormat from 'react-number-format'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { validateUEN } from '../../../../_metronic/helpers/validation'
import { fetchStartWithForward } from '../../../../redux/actions/common'
import { saveApplicationDetails, setLoanApplication, setLoanStep } from '../redux/actions'
import { trim } from '../../../../_metronic/helpers'
import { MaintenanceModeBanner } from '../../../../_metronic/helpers/components/MaintenanceModeBanner'
import { getStoredEnv } from '../../../../utils/getIntegrationURLFromENV'
import { RightAeroIcon } from '../../../../svg/RightAeroIcon'
import { useHistory } from 'react-router-dom'
import { CountryList } from '../../../../utils/constants/country-list'
import { OverlayTrigger } from 'react-bootstrap-v5'
import { Popover } from 'react-bootstrap'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import { AlexiApplicationTypes } from '../../../../utils/constants/AlexiApplicationType.enum'
import { LeftArowIcon } from '../../../../svg/LeftArowIcon'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { parsePhoneNumberWithError } from 'libphonenumber-js/max'
import { StaticCountrySelect } from '../../../../components/FixedSingaporePhoneComponent/StaticCountrySelect'

const errorMsg = {
  required: 'required',
  amountRange: 'Amount range should be between 100,000 - 3,000,000',
  durationRange: 'Duration range should be between 3 - 36 months',
}

const Step1Sub1: FC<any> = ({ createApplication, updateApplication, mobileNo, agentCode }) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const maxAmount = 3000000
  const minAmount = 100000
  const maxDuration = 36
  const minDuration = 3

  const [loanAmountRange, setLoanAmountRange] = useState<any>(100000)
  const [loanDurationRange, setLoanDurationRange] = useState<any>(6)
  const [countryOfIncorporationCode, setCountryOfIncorporationCode] = useState<any>(null)

  // const [mobileNo, setMobileNo] = useState<any>('+977984');
  // const [sendOTP, {data,loading,error}] = useMutation(mutations.SEND_OTP, { variables: { mobileNo, isApplicantOrShareholder: true } });

  const savedApplicationDetails = useSelector((state: any) => state.loan.loanApplication)

  const handleLoanAmountRange = (value: string) => {
    dispatch(saveApplicationDetails(formik.values))
    dispatch(setLoanApplication(formik.values))
    setLoanAmountRange(value)
    formik.setFieldValue('amountRequested', value)
    //if(error.match(/ name/i)) formik.setFieldError('name', error);
  }

  const handleLoanDurationRange = (value: string) => {
    dispatch(saveApplicationDetails(formik.values))
    dispatch(setLoanApplication(formik.values))
    setLoanDurationRange(value)
    formik.setFieldValue('durationInMonths', value)
  }

  const initialValues = {
    applicantName: '',
    // applicationStatus: '',
    companyUEN: '',
    positionInCompany: '',
    amountRequested: loanAmountRange,
    durationInMonths: loanDurationRange,
    mibCountryOfIncorporation: 'Singapore',
    mobileNumber: '',
    promoCode: '',
    businessOwnerEmail: '',
  }

  const validationSchema = Yup.object().shape({
    amountRequested: Yup.number()
      .required(errorMsg.required)
      .min(minAmount, errorMsg.amountRange)
      .max(maxAmount, errorMsg.amountRange),
    durationInMonths: Yup.number()
      .required(errorMsg.required)
      .min(minDuration, errorMsg.durationRange)
      .max(maxDuration, errorMsg.durationRange),
    companyUEN: Yup.string()
      .required('Company UEN is required')
      .test('unique', 'Invalid UEN. Kindly recheck the entered UEN', (value: any) =>
        validateUEN(value)
      ),
    applicantName: Yup.string()
      .required('Company Owner Name is required')
      .matches(
        /^[A-Za-z\s.']+$/,
        'Please enter a valid Company Owner Name using letters, spaces, periods, or apostrophes, and include at least 2 letters.'
      )
      .test(
        'at-least-two-letters',
        'Company Owner Name cannot contain only special characters. Please enter a valid company owner name.',
        (value: any) => {
          if (!value) return false
          const letters = value.match(/[A-Za-z]/g)
          return (letters?.length || 0) >= 2
        }
      ),
    businessOwnerEmail: Yup.string()
      .required('Company Owner Email Address is required')
      .email('Enter Valid Company Owner Email'),
    promoCode: Yup.string(),
    mibCountryOfIncorporation: Yup.string().required('Country of Incorporation is required'),

    mobileNumber: Yup.string()
      .required('Company Owner Mobile Number is required')
      .test('mobileValidation', 'Please enter valid Singapore number', (value: any) => {
        if (!value) return false
        // Use libphonenumber-js to validate phone number format
        const number = parsePhoneNumberWithError(value, 'SG')
        // Use libphonenumber-js to validate phone number format
        return isValidPhoneNumber(value, 'SG') && number.isValid() && number.country === 'SG'
      }),
    mibCompanyName: Yup.string()
      .required('Company Name is required')
      .min(2, 'Company Name must be at least 2 characters')
      .max(100, 'Company Name must be less than 100 characters')
      .test('no-only-spaces', 'Company Name cannot contain only spaces', value => {
        return value ? value.trim().length > 0 : false
      })
      .test('no-only-numbers', 'Company Name cannot contain only numbers', value => {
        return value ? !/^\d+$/.test(value.trim()) : false
      })
      .test('not-only-special-chars', 'Company Name cannot contain only special characters', value => {
        if (!value) return true // Skip if empty
        // Check that the value contains at least one letter or number

        return /[a-zA-Z0-9]/.test(value.trim())
      }),
    //TODO: add validation on prod
    // ,(value:any) => {
    //   if(/^\+65(8|9)\d{7}$/g.test(value) || /^\+977\d{10}$/g.test(value)){
    //     return true
    //   }
    //   return false
    // })
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: savedApplicationDetails || initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        const {
          amountRequested,
          durationInMonths,
          companyUEN,
          positionInCompany,
          applicantName,
          mobileNumber,
          promoCode,
          mibCountryOfIncorporation,
          businessOwnerEmail,
          mibCompanyName
        } = trim(values)

        dispatch(fetchStartWithForward())

        if (
          localStorage.getItem('loanType') === null ||
          localStorage.getItem('loanType') === undefined
        ) {
          localStorage.setItem('loanType', AlexiApplicationTypes.LOAN_APPLICATION)
          localStorage.setItem('applicationType', AlexiApplicationTypes.AGENT_CLIENT_ONBOARDING)
        }
        if (
          savedApplicationDetails === null ||
          savedApplicationDetails?.id === null ||
          savedApplicationDetails?.id === undefined
        ) {
          await createApplication({
            variables: {
              data: {
                amountRequested: amountRequested * 1,
                durationInMonths: durationInMonths * 1,
                companyUEN,
                mibCompanyUEN: companyUEN,
                agentCode: agentCode,
                applicantName,
                businessOwnerEmail,
                mobileNumber,
                promoCode,
                mibCountryOfIncorporation,
                applicationStatus: 'in-progress',
                applicationCompleteStep: 1.1,
                applicationType:
                  localStorage.getItem('loanType') || localStorage.getItem('applicationType') || AlexiApplicationTypes.AGENT_CLIENT_ONBOARDING,
                partnerId: localStorage.getItem('partnerId') || '',
                credilinqEnvironment: getStoredEnv(),
                mibCompanyName: mibCompanyName
              },
            },
          })
        } else {
          await updateApplication({
            variables: {
              data: {
                amountRequested: amountRequested * 1,
                durationInMonths: durationInMonths * 1,
                companyUEN,
                mibCompanyUEN: companyUEN,
                agentCode: agentCode,
                positionInCompany,
                applicantName,
                businessOwnerEmail,
                mobileNumber,
                promoCode,
                mibCountryOfIncorporation,
                mibCompanyName,
              },
              where: {
                id: savedApplicationDetails?.id,
              },
            },
          })
          dispatch(setLoanStep(2))
          history.replace(`/agent-application/${savedApplicationDetails.id}?step=2`)
        }
      } catch (error) {
        console.error('Error creating application:', error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    const countryCode = CountryList.filter(
      (country) => country.name === formik.values.mibCountryOfIncorporation
    )[0]?.code
    setCountryOfIncorporationCode(countryCode)
  }, [formik.values.mibCountryOfIncorporation])

  // const { id } = useParams<{ id: string }>()

  function backLink() {
    history.push(`/agent-application/view/dashboard`)
  }

  return (
    <div className='d-flex flex-column flex-column-fluid'>
      <MaintenanceModeBanner />

      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='cardNew'>
          <div className='section-heading'>
            <h2>Loan Details</h2>
            <hr className='text-gray-400' />
          </div>

          <div className='row'>
            <div className='col-md-6'>
              <label htmlFor='' className='input-field-label required'>
                I want to borrow
              </label>
              <NumberFormat
                name='amountRequested'
                value={formik.values.amountRequested}
                className='form-control form-control-solid'
                // displayType={'text'}
                thousandSeparator={true}
                prefix={'SG$'}
                allowNegative={true} // Explicitly allow negative values
                isNumericString={true} // Treat the input as a numeric string
                onValueChange={(values: any) => {
                  let { value } = values
                  // Ensure we accept any value including negative
                  formik.setFieldValue('amountRequested', value)
                }}
                onBlur={(event: any) => {
                  let { value } = event.target
                  // For negative values, we need special handling since the prefix is at the beginning
                  // Check if the value contains a negative sign
                  const isNegative = event.target.value.includes('-')

                  // Remove the prefix (SG$) and any commas
                  if (isNegative) {
                    // For negative values, the format might be -SG$200,000 or SG$-200,000
                    value = value.replace(/[SG$,]/g, '')
                  } else {
                    value = value.substring(3) // Remove prefix
                    value = value.replace(/[,$]/g, '') // Remove commas and any dollar signs
                  }

                  let parsedValue = value ? parseFloat(value) : 0

                  // Always keep the original entered value, including negative values
                  formik.setFieldValue('amountRequested', parsedValue)
                  // Show appropriate validation errors
                  if (Number(parsedValue) < 0) {
                    formik.setFieldError('amountRequested', 'Amount cannot be negative')
                    formik.setFieldValue('amountRequested', parsedValue)
                  } else if (Number(parsedValue) === 0) {
                    formik.setFieldError('amountRequested', 'Amount cannot be zero')
                    formik.setFieldValue('amountRequested', parsedValue)
                  } else if (Number(parsedValue) < minAmount) {
                    formik.setFieldError(
                      'amountRequested',
                      `Amount cannot be less than SG$${minAmount.toLocaleString()}`
                    )
                    formik.setFieldValue('amountRequested', parsedValue)
                  } else if (Number(parsedValue) > maxAmount) {
                    formik.setFieldError(
                      'amountRequested',
                      `Amount cannot exceed SG$${maxAmount.toLocaleString()}`
                    )
                    formik.setFieldValue('amountRequested', parsedValue)
                  } else {
                    // Only round valid values
                    let roundedValue = Math.round(Number(parsedValue) / 500) * 500
                    formik.setFieldValue('amountRequested', roundedValue)
                  }

                  formik.setFieldTouched('amountRequested', true)
                }}
              />
              {formik.touched.amountRequested && formik.errors.amountRequested && (
                <div className='text-danger mt-2'>{formik.errors.amountRequested}</div>
              )}
              <RangeSlider
                // id="amountRequested"
                min={minAmount}
                max={maxAmount}
                step={5000}
                tooltip='on'
                tooltipPlacement='top'
                tooltipLabel={(currentValue) => `SG$ ${currentValue}`}
                value={formik.values.amountRequested}
                onChange={(event: any) => {
                  handleLoanAmountRange(event.target.value)
                }}
                className='rangeSlider'
                // onChange={({target:{value}}) => { if(regexNumber.test(value)) formik.setFieldValue('total', value) }}
                // onChange={formik.handleChange}
              />
              <div className='d-flex flex-row justify-content-between'>
                <div>SG$ 100,000</div>
                <div>SG$ 3,000,000</div>
              </div>
            </div>
            <div className='col-md-6'>
              <label htmlFor='' className='input-field-label required'>
                How long for?
              </label>
              <input
                name='durationInMonths'
                type='text'
                className='form-control form-control-solid'
                value={
                  formik.values.durationInMonths
                    ? `${formik.values.durationInMonths} ${
                        formik.values.durationInMonths === 1 ? 'Month' : 'Months'
                      }`
                    : ''
                }
                onChange={(e) => {
                  // Extract numbers from input
                  const value = e.target.value.replace(/\D/g, '')
                  if (value === '') {
                    formik.setFieldValue('durationInMonths', '')
                    return
                  }
                  const numValue = parseInt(value)
                  if (!isNaN(numValue)) {
                    formik.setFieldValue('durationInMonths', numValue)
                  }
                }}
                onBlur={(e) => {
                  let value = parseInt(e.target.value.replace(/\D/g, '') || '0')
                  value = Math.min(maxDuration, Math.max(minDuration, value || minDuration))
                  formik.setFieldValue('durationInMonths', value)
                }}
                onFocus={(e) => {
                  // Show only the number when focused for easier editing
                  const value = formik.values.durationInMonths
                  if (value) {
                    e.target.value = value.toString()
                  }
                }}
              />
              {formik.touched.durationInMonths && formik.errors.durationInMonths && (
                <div className='text-danger mt-2'>{formik.errors.durationInMonths}</div>
              )}
              <RangeSlider
                // id="durationInMonths"
                min={3}
                max={36}
                tooltip='on'
                tooltipPlacement='top'
                tooltipLabel={(currentValue) => `${currentValue} months`}
                value={formik.values.durationInMonths}
                // onChange={formik.handleChange}
                onChange={(event: any) => {
                  handleLoanDurationRange(event.target.value)
                }}
                className='rangeSlider'
              />
              <div className='d-flex flex-row justify-content-between'>
                <div>3 months</div>
                <div>36 months</div>
              </div>
            </div>
          </div>
        </div>
        <div className='cardNew'>
          <div className='section-heading'>
            <h2>Company Details</h2>
            <hr className='text-gray-400' />
          </div>
          <div className='row'>
            <div className='col-md-6 mb-5'>
              {' '}
              <label htmlFor='' className='input-field-label required'>
                Company Name{' '}
              </label>
              <input
                name='mibCompanyName'
                type='text'
                className='form-control form-control-solid'
                value={formik.values.mibCompanyName}
                onChange={formik.handleChange}
                placeholder='Enter Company Name'
              />
              {(formik.touched.mibCompanyName || formik.submitCount > 0) && formik.errors.mibCompanyName && (
                <div className='text-danger mt-2'>{formik.errors.mibCompanyName}</div>
              )}
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Company UEN
              </label>
              <input
                name='companyUEN'
                type='text'
                className='form-control form-control-solid'
                value={formik.values.companyUEN}
                onChange={formik.handleChange}
                placeholder='Enter Company UEN'
              />
              {formik.touched.companyUEN && formik.errors.companyUEN && (
                <div className='text-danger mt-2'>{formik.errors.companyUEN}</div>
              )}
            </div>

          </div>
          <div className='row'>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Country of Incorporation
              </label>
              <Autocomplete
                size='small'
                id='tags-outlined'
                className='countryList mt-2'
                popupIcon={<KeyboardArrowDownOutlinedIcon />}
                options={CountryList}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                value={
                  formik.values.mibCountryOfIncorporation
                    ? CountryList.find((c) => c.name === formik.values.mibCountryOfIncorporation) || null
                    : null
                }
                onChange={(event, newValue) => {
                  formik.setFieldValue('mibCountryOfIncorporation', newValue?.name || '')
                }}
                renderOption={(props, option) => {
                  const { ...optionProps } = props
                  return (
                    <Box
                      component='li'
                      sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                      {...optionProps}
                    >
                      <img
                        loading='lazy'
                        width='20'
                        srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                        src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                        alt=''
                      />
                      {option.name}
                    </Box>
                  )
                }}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: countryOfIncorporationCode ? (
                          <img
                            loading='lazy'
                            width='25'
                            style={{ marginRight: 8 }}
                            src={`https://flagcdn.com/w20/${countryOfIncorporationCode.toLowerCase()}.png`}
                            alt=''
                          />
                        ) : null,
                      }}
                      placeholder='Select Country'
                    />
                  )
                }}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                placeholder='Select'
              />

              {formik.touched.mibCountryOfIncorporation &&
                formik.errors.mibCountryOfIncorporation && (
                  <div className='text-danger mt-2'>{formik.errors.mibCountryOfIncorporation}</div>
                )}
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Company Owner Name{' '}
                <OverlayTrigger
                  trigger='hover'
                  overlay={
                    <Popover>
                      <Popover.Body
                        style={{
                          backgroundColor: '#202020',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '15px',
                        }}
                      >
                        Kindly provide the full name of the company owner (not the company name)
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 14 14'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M7 5H7.00667M6.33333 7H7V9.66667H7.66667M1 7C1 7.78793 1.15519 8.56815 1.45672 9.2961C1.75825 10.0241 2.20021 10.6855 2.75736 11.2426C3.31451 11.7998 3.97595 12.2417 4.7039 12.5433C5.43185 12.8448 6.21207 13 7 13C7.78793 13 8.56815 12.8448 9.2961 12.5433C10.0241 12.2417 10.6855 11.7998 11.2426 11.2426C11.7998 10.6855 12.2417 10.0241 12.5433 9.2961C12.8448 8.56815 13 7.78793 13 7C13 5.4087 12.3679 3.88258 11.2426 2.75736C10.1174 1.63214 8.5913 1 7 1C5.4087 1 3.88258 1.63214 2.75736 2.75736C1.63214 3.88258 1 5.4087 1 7Z'
                      stroke='var(--primary-color)'
                      stroke-width='1.5'
                      stroke-linecap='round'
                      stroke-linejoin='round'
                    />
                  </svg>
                </OverlayTrigger>
              </label>
              <input
                name='applicantName'
                type='text'
                className='form-control form-control-solid'
                value={formik.values.applicantName}
                onChange={formik.handleChange}
                placeholder='Kindly provide the full name of the company owner'
              />
              {formik.touched.applicantName && formik.errors.applicantName && (
                <div className='text-danger mt-2'>{formik.errors.applicantName}</div>
              )}
            </div>
          </div>

          <div className='row'>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Company Owner Mobile Number
              </label>
              <PhoneInput
                id='mobileNumber'
                international
                country='SG'
                defaultCountry='SG'
                countryCallingCodeEditable={false}
                countrySelectComponent={StaticCountrySelect}
                value={formik.values.mobileNumber}
                onChange={(value) => formik.setFieldValue('mobileNumber', value)}
                placeholder='Enter Company Mobile Number'
                className='int-phone-input'
              />
              {(formik.touched.mobileNumber || formik.values.mobileNumber === undefined) &&
                formik.errors.mobileNumber && (
                  <div className='text-danger mt-2'>{formik.errors.mobileNumber}</div>
                )}
            </div>
            <div className='col-md-6 mb-5'>
              {' '}
              <label htmlFor='' className='input-field-label required'>
                Company Owner Email{' '}
              </label>
              <input
                name='businessOwnerEmail'
                type='text'
                className='form-control form-control-solid'
                value={formik.values.businessOwnerEmail}
                onChange={formik.handleChange}
                placeholder='Enter Company Owner Email'
              />
              {formik.touched.businessOwnerEmail && formik.errors.businessOwnerEmail && (
                <div className='text-danger mt-2'>{formik.errors.businessOwnerEmail}</div>
              )}
            </div>
          </div>
          <div className='row'>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label'>
                Channel Partner Assigned Code (optional)
              </label>
              <input
                name='promoCode'
                type='text'
                className='form-control form-control-solid'
                value={formik.values.promoCode}
                onChange={formik.handleChange}
                placeholder='Channel Partner Assigned Code'
              />
            </div>
          </div>
        </div>

        <div className='cardNew stickeyFooter'>
          <div className={`d-flex justify-content-between`}>
            <button type='button' className='button button-outline-primary' onClick={backLink}>
              <LeftArowIcon color='currentColor' fontSize={12} />
              Back
            </button>
            <button type='submit' className='button button-primary' disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Sending...' : 'Save and Continue'}{' '}
              <RightAeroIcon color='currentColor' />
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export { Step1Sub1 }
